import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { Button } from "@/components/ui/button";
import { breadcrumbSchema, faqSchema, jsonLd, SITE_URL, absUrl } from "@/lib/seo";
import { assertSalonDataConsistency, salonStatsFor } from "@/lib/salonStats";
import { SalonRatingLines } from "@/components/SalonRatingLines";
import { formatCount, formatRating, salonReviewStats } from "@/data/reviewStats";
import { withBookingUtm, GENERIC_BOKADIREKT } from "@/lib/booking";
import { internalPath } from "@/lib/paths";
import { ContextLinkBlock } from "@/components/ContextLinkBlock";
import { SERVICE_SALON_LINKS } from "@/data/localServiceLinks";


type SalonKey = "ahlens-city" | "taby" | "akersberga" | "arninge";

const ORDER: SalonKey[] = ["ahlens-city", "taby", "akersberga", "arninge"];

const LOCAL_INFO: Record<
  SalonKey,
  { lage: string; transport: string[] }
> = {
  "ahlens-city": {

    lage: "Klarabergsgatan 50, plan 3",
    transport: [
      "1 minut från T-Centralen (röd, grön och blå linje)",
      "2 minuter från Stockholm Central",
      "Ingen parkering – men stadens bästa kollektivtrafikläge",
    ],
  },
  taby: {
    lage: "Biblioteksgången 1, Westfield Täby Centrum",
    transport: [
      "Roslagsbanan till Täby Centrum",
      "Bussterminal direkt i centrumet",
      "Parkeringsgarage i Westfield Täby Centrum",
    ],
  },
  akersberga: {
    lage: "Långhundravägen 9, ICA Maxi",
    transport: [
      "Roslagsbanan till Åkersberga station",
      "Gratis parkering direkt vid ICA Maxi",
    ],
  },
  arninge: {
    lage: "Kundvägen 1D, ICA Maxi",
    transport: [
      "Buss 619 från Täby Centrum",
      "Arninge resecentrum ligger intill",
      "Gratis parkering vid ICA Maxi",
    ],
  },
};

assertSalonDataConsistency(LOCAL_INFO);

const rows = salonStatsFor(ORDER).map((stats) => ({
  ...stats,
  info: LOCAL_INFO[stats.key],
}));

const byKey = Object.fromEntries(rows.map((r) => [r.key, r])) as Record<SalonKey, (typeof rows)[number]>;

const mostBarbers = rows.reduce((a, b) => (b.barbers > a.barbers ? b : a));

function googleLine(slug: SalonKey, withUnit: boolean): string {
  const s = salonReviewStats[slug].google;
  return `${formatRating(s.rating)}${withUnit ? " av 5" : ""} (${formatCount(s.count)}${withUnit ? " omdömen" : ""})`;
}

function bokadirektLine(slug: SalonKey, withUnit: boolean): string {
  const s = salonReviewStats[slug].bokadirekt;
  return `${formatRating(s.rating)}${withUnit ? " av 5" : ""} (${formatCount(s.count)}${withUnit ? " omdömen" : ""})`;
}

const ratingFaqAnswer = `På Google: Åhléns City ${googleLine("ahlens-city", true)}, Täby Centrum ${googleLine("taby", false)}, Åkersberga ${googleLine("akersberga", false)}, Arninge ${googleLine("arninge", false)}. På BokaDirekt: Åhléns City ${bokadirektLine("ahlens-city", true)}, Täby Centrum ${bokadirektLine("taby", false)}, Åkersberga ${bokadirektLine("akersberga", false)}, Arninge ${bokadirektLine("arninge", false)}. Vi visar alltid källorna var för sig.`;

const guidance = [
  {
    situation: "Jobbar eller pendlar genom city",
    answer: "Åhléns City – du hinner klippa dig på lunchen, en minut från T-Centralen.",
  },
  {
    situation: "Bor i Täby, Danderyd eller Vallentuna",
    answer: `Täby Centrum – störst team (${byKey.taby.barbers} barberare) och flest lediga tider.`,
  },
  {
    situation: "Bor i Österåker eller Vaxholm",
    answer: `Åkersberga, ${formatRating(salonReviewStats.akersberga.google.rating)} av 5 på Google (${formatCount(salonReviewStats.akersberga.google.count)} omdömen) och gratis parkering.`,
  },
  {
    situation: "Kommer med bil",
    answer: "Åkersberga eller Arninge – gratis parkering direkt utanför.",
  },
  {
    situation: "Vill kombinera med shopping eller ärenden",
    answer: "Täby Centrum, Åkersberga eller Arninge – alla ligger i köpcentrum.",
  },
  {
    situation: "Behöver söndagsöppet",
    answer: "Alla fyra salonger har öppet på söndagar – Åhléns City öppnar 11, övriga 10.",
  },
];

const faq = [
  {
    q: "Vilken av era salonger ligger närmast T-Centralen?",
    a: "Åhléns City på Klarabergsgatan 50, plan 3. Det är cirka 1 minut till fots från T-Centralen (röd, grön och blå linje) och 2 minuter från Stockholm Central.",
  },
  {
    q: "Har ni herrklippning med drop-in i Stockholm?",
    a: "Ja, alla fyra salonger tar emot drop-in i mån av tid. Vill du vara säker på en tid bokar du online via BokaDirekt.",
  },
  {
    q: "Vilken salong har flest lediga tider?",
    a: `${mostBarbers.salon.shortName} – där arbetar ${mostBarbers.barbers} barberare, vilket är vårt största team och ger flest tillgängliga tider.`,
  },
  {
    q: "Finns det parkering vid era salonger?",
    a: "Åkersberga och Arninge har gratis parkering direkt vid ICA Maxi. Täby Centrum har parkeringsgarage i Westfield. Åhléns City har ingen egen parkering – ta tunnelbanan eller pendeltåget dit.",
  },
  {
    q: "Vilken salong har högst betyg?",
    a: ratingFaqAnswer,
  },
  {
    q: "Vad kostar en herrklippning i Stockholm?",
    a: "Herrklippning kostar 450 kr och det är samma pris på alla fyra salonger i Stockholm.",
  },
  {
    q: "Hur bokar jag herrklippning i Stockholm?",
    a: "Du bokar online via BokaDirekt på den salong som passar dig bäst, eller kommer på drop-in i mån av tid.",
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Herrklippning Stockholm",
  description:
    "Herrklippning i Stockholm på fyra salonger – Åhléns City, Täby Centrum, Åkersberga och Arninge. 450 kr, samma pris överallt.",
  provider: { "@type": "BarberShop", name: "Tailor's Barber", url: SITE_URL },
  areaServed: { "@type": "City", name: "Stockholm" },
  offers: { "@type": "Offer", price: "450", priceCurrency: "SEK" },
};

export const Route = createFileRoute("/herrklippning-stockholm")({
  head: () => ({
    meta: [
      { title: "Herrklippning Stockholm – hitta rätt salong | Tailor's Barber" },
      {
        name: "description",
        content:
          "Jämför våra fyra salonger för herrklippning i Stockholm: läge, öppettider, betyg och hur du tar dig hit. Åhléns City, Täby Centrum, Åkersberga och Arninge – 450 kr.",
      },
      { property: "og:title", content: "Herrklippning Stockholm – hitta rätt salong" },
      {
        property: "og:description",
        content:
          "Fyra salonger i Stockholm för herrklippning. Jämför läge, öppettider, team och betyg – och se hur du tar dig hit.",
      },
      { property: "og:url", content: absUrl("/herrklippning-stockholm") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: absUrl("/herrklippning-stockholm") }],
    scripts: [
      jsonLd(serviceSchema),
      jsonLd(faqSchema(faq)),
      jsonLd(
        breadcrumbSchema([
          { name: "Hem", url: "/" },
          { name: "Herrklippning Stockholm", url: "/herrklippning-stockholm" },
        ]),
      ),
    ],
  }),
  component: HerrklippningStockholmPage,
});

function HerrklippningStockholmPage() {
  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-4">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Herrklippning Stockholm</span>
        </nav>
        <div className="eyebrow">Herrklippning Stockholm</div>
        <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
          Herrklippning i Stockholm – vilken salong ska du gå till?
        </h1>
        <p className="mt-5 text-lg text-foreground/80 max-w-prose leading-relaxed">
          Tailor&apos;s Barber har fyra salonger i Stockholmsregionen: Åhléns City, Täby Centrum,
          Åkersberga och Arninge. Här jämför du läge, öppettider, team och betyg, och ser hur du
          enklast tar dig hit. Herrklippning kostar 450 kr, samma pris på alla salonger. Vårt mest
          centrala läge är{" "}
          <Link to={internalPath("/barberare-stockholm-ahlens-city/")} className="underline font-medium">
            Åhléns City
          </Link>{" "}
          på Klarabergsgatan 50, en minut från T-Centralen.
        </p>

        <div className="mt-6 card-elev p-5 max-w-3xl">
          <p className="text-base text-foreground/85">
            Vill du veta vad som ingår i klippningen, vilka tekniker vi arbetar med och hur du väljer
            stil?{" "}
            <Link to="/herrklippning/" className="underline font-medium">
              Läs allt om herrklippning som tjänst
            </Link>
            .
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild size="lg">
            <a href={GENERIC_BOKADIREKT} target="_blank" rel="noopener noreferrer">
              Boka herrklippning
            </a>
          </Button>
          <Link to="/priser/" className="btn-outline">Se prislista</Link>
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Jämför våra fyra salonger i Stockholm</h2>

        {/* Desktop table */}
        <div className="mt-6 hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <caption className="sr-only">
              Jämförelse av Tailor&apos;s Barbers fyra salonger i Stockholm
            </caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="py-3 pr-4 font-medium">Salong</th>
                <th scope="col" className="py-3 pr-4 font-medium">Läge</th>
                <th scope="col" className="py-3 pr-4 font-medium">Öppettider</th>
                <th scope="col" className="py-3 pr-4 font-medium">Barberare</th>
                <th scope="col" className="py-3 pr-4 font-medium">Betyg</th>
                <th scope="col" className="py-3 font-medium">
                  <span className="sr-only">Boka</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-border/60 align-top">
                  <th scope="row" className="py-4 pr-4 font-medium">
                    <Link to={internalPath(r.salon.url)} className="underline">{r.salon.shortName}</Link>
                  </th>
                  <td className="py-4 pr-4 text-foreground/80">{r.info.lage}</td>
                  <td className="py-4 pr-4 text-foreground/80">
                    Vardagar {r.vardagar}
                    <br />
                    {r.helg}
                  </td>
                  <td className="py-4 pr-4 text-foreground/80">{r.barbers}</td>
                  <td className="py-4 pr-4 text-foreground/80">
                    <SalonRatingLines salon={r.key} compact />
                  </td>
                  <td className="py-4">
                    <Button asChild size="sm">
                      <a href={withBookingUtm(r.salon.bokaDirektUrl)} target="_blank" rel="noopener noreferrer">
                        Boka {r.salon.shortName}
                      </a>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="mt-6 grid gap-4 md:hidden">
          {rows.map((r) => (
            <article key={r.key} className="card-elev p-5">
              <h3 className="text-lg font-medium">
                <Link to={internalPath(r.salon.url)} className="underline">{r.salon.shortName}</Link>
              </h3>
              <dl className="mt-3 space-y-1.5 text-sm text-foreground/80">
                <div className="flex gap-2"><dt className="font-medium min-w-24">Läge</dt><dd>{r.info.lage}</dd></div>
                <div className="flex gap-2"><dt className="font-medium min-w-24">Vardagar</dt><dd>{r.vardagar}</dd></div>
                <div className="flex gap-2"><dt className="font-medium min-w-24">Helg</dt><dd>{r.helg}</dd></div>
                <div className="flex gap-2"><dt className="font-medium min-w-24">Barberare</dt><dd>{r.barbers}</dd></div>
                <div className="flex gap-2"><dt className="font-medium min-w-24">Betyg</dt><dd><SalonRatingLines salon={r.key} compact /></dd></div>
              </dl>
              <div className="mt-4">
                <Button asChild size="sm">
                  <a href={withBookingUtm(r.salon.bokaDirektUrl)} target="_blank" rel="noopener noreferrer">
                    Boka {r.salon.shortName}
                  </a>
                </Button>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-6 text-foreground/70 max-w-3xl">
          Herrklippning 450 kr – samma pris på alla salonger. Se hela{" "}
          <Link to="/priser/" className="underline">prislistan</Link>.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Så tar du dig hit</h2>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {rows.map((r) => (
            <article key={r.key} className="card-elev p-6">
              <h3 className="text-xl font-medium">{r.salon.shortName}</h3>
              <p className="mt-1 text-sm text-foreground/70">{r.info.lage}</p>
              <ul className="mt-3 space-y-1.5 text-sm text-foreground/85 list-disc list-inside">
                {r.info.transport.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button asChild size="sm">
                  <a href={withBookingUtm(r.salon.bokaDirektUrl)} target="_blank" rel="noopener noreferrer">
                    Boka {r.salon.shortName}
                  </a>
                </Button>
                <Link to={internalPath(r.salon.url)} className="btn-outline text-sm">
                  Om salongen i {r.salon.shortName}
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Vilken salong passar dig?</h2>
        <ul className="mt-6 grid gap-3 md:grid-cols-2 max-w-4xl">
          {guidance.map((g) => (
            <li key={g.situation} className="card-elev p-5">
              <strong>{g.situation}</strong>
              <p className="mt-1 text-sm text-foreground/80">{g.answer}</p>
            </li>
          ))}
        </ul>
      </section>

      <ContextLinkBlock
        heading={SERVICE_SALON_LINKS["herrklippning-stockholm"]!.heading}
        intro={SERVICE_SALON_LINKS["herrklippning-stockholm"]!.intro}
        links={SERVICE_SALON_LINKS["herrklippning-stockholm"]!.links}
      />

      <Faq items={faq} heading="Vanliga frågor om herrklippning i Stockholm" />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Relaterade sidor</h2>
        <ul className="mt-4 flex flex-wrap gap-3">
          <li><Link to="/herrklippning/" className="btn-outline">Herrklippning – så går det till</Link></li>
          <li><Link to="/taper-fade/" className="btn-outline">Taper Fade</Link></li>
          <li><Link to="/skaggtrimning/" className="btn-outline">Skäggtrimning</Link></li>
          <li><Link to="/" className="btn-outline">Barberare Stockholm</Link></li>
        </ul>
      </section>

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Boka din herrklippning</h2>
          <p className="mt-3 text-foreground/80">
            Välj salong och boka online – eller kom förbi på drop-in i mån av tid.
          </p>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={GENERIC_BOKADIREKT} target="_blank" rel="noopener noreferrer">
                Boka herrklippning
              </a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
